import { useEffect, useState } from 'react'

import { useCheckInternetConnection } from '@/hooks'
import { useTranslate } from '@/i18n/useTranslate'
import { getReferralEnabled } from '@/services/deviceConfigurationService'
import { referralService } from '@/services/referralService'
import { ReferralOutput } from '@/types/pos/referral'
import {
    isContextTokenActive,
    storeReferralDashboardToken
} from '@/utilities/referralHelper'

type ReferralIntroTemplateProps = {
    referral: ReferralOutput
    onFinish: () => void
}

type ActionFeedback = {
    type: 'success' | 'error'
    message: string
} | null

const getErrorMessage = (error: unknown, fallback: string) => {
    if (
        error &&
        typeof error === 'object' &&
        'errors' in error &&
        Array.isArray((error as { errors: { description?: string }[] }).errors)
    ) {
        const first = (error as { errors: { description?: string }[] })
            .errors[0]
        if (first?.description) return first.description
    }
    if (error instanceof Error && error.message) return error.message
    return fallback
}

export default function ReferralIntroTemplate({
    referral,
    onFinish
}: ReferralIntroTemplateProps) {
    const { t } = useTranslate()
    const { deviceConfiguration } = useCheckInternetConnection()
    const referralEnabled = getReferralEnabled(deviceConfiguration)
    const [phoneNumber, setPhoneNumber] = useState('')
    const [isSendingSms, setIsSendingSms] = useState(false)
    const [smsFeedback, setSmsFeedback] = useState<ActionFeedback>(null)
    const [canUseSms, setCanUseSms] = useState(() =>
        isContextTokenActive(referral)
    )

    useEffect(() => {
        if (!referralEnabled) {
            onFinish()
        }
    }, [onFinish, referralEnabled])

    useEffect(() => {
        setCanUseSms(isContextTokenActive(referral))
        if (!referral.contextTokenExpiresAt) return

        const expiry = Date.parse(referral.contextTokenExpiresAt)
        if (Number.isNaN(expiry)) return

        const remaining = expiry - Date.now()
        if (remaining <= 0) {
            setCanUseSms(false)
            return
        }

        const timer = window.setTimeout(() => setCanUseSms(false), remaining)
        return () => window.clearTimeout(timer)
    }, [referral])

    const appendPhoneDigit = (digit: number) => {
        setPhoneNumber((prev) => `${prev}${digit}`.slice(0, 15))
        setSmsFeedback(null)
    }

    const deletePhoneDigit = () => {
        setPhoneNumber((prev) => prev.slice(0, -1))
        setSmsFeedback(null)
    }

    const onSendSms = async () => {
        if (
            !referralEnabled ||
            !canUseSms ||
            !referral.contextToken ||
            !phoneNumber ||
            isSendingSms
        )
            return

        setIsSendingSms(true)
        setSmsFeedback(null)
        try {
            const response = await referralService.shareReferralBySms({
                contextToken: referral.contextToken,
                mobileNumber: phoneNumber
            })
            if (response.dashboardToken) {
                storeReferralDashboardToken(response.dashboardToken)
            }
            setSmsFeedback({
                type: 'success',
                message:
                    response.message || t('referralIntro.smsSuccessFallback')
            })
        } catch (error) {
            setSmsFeedback({
                type: 'error',
                message: getErrorMessage(
                    error,
                    t('referralIntro.smsErrorFallback')
                )
            })
        } finally {
            setIsSendingSms(false)
        }
    }

    if (!referralEnabled) {
        return null
    }

    return (
        <div className="referral-intro">
            <div className="referral-intro__header">
                <h1>{t('referralIntro.title')}</h1>
                <p className="referral-intro__subtitle">
                    {t('referralIntro.subtitle')}
                </p>
            </div>

            <section className="referral-intro__panel referral-intro__panel--sms">
                <div className="referral-intro__sms-copy">
                    <p className="referral-intro__panel-label">
                        {t('referralIntro.smsLabel')}
                    </p>
                    <p className="referral-intro__panel-text">
                        {t('referralIntro.smsText')}
                    </p>
                    {!canUseSms && (
                        <p className="referral-intro__expired">
                            {t('referralIntro.tokenExpired')}
                        </p>
                    )}
                </div>

                <div className="referral-intro__sms-controls">
                    <div className="referral-intro__input-wrap">
                        <input
                            value={phoneNumber}
                            readOnly
                            className="referral-intro__input"
                            placeholder={t('referralIntro.smsPlaceholder')}
                        />
                        <button
                            type="button"
                            className="referral-intro__input-clear"
                            onClick={() => {
                                setPhoneNumber('')
                                setSmsFeedback(null)
                            }}
                            aria-label={t('referralIntro.clear')}>
                            ×
                        </button>
                    </div>

                    <div className="referral-intro__keypad">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                            <button
                                key={digit}
                                type="button"
                                onClick={() => appendPhoneDigit(digit)}
                                className="referral-intro__key"
                                disabled={!canUseSms}>
                                {digit}
                            </button>
                        ))}
                        <div />
                        <button
                            type="button"
                            onClick={() => appendPhoneDigit(0)}
                            className="referral-intro__key"
                            disabled={!canUseSms}>
                            0
                        </button>
                        <button
                            type="button"
                            onClick={deletePhoneDigit}
                            className="referral-intro__key referral-intro__key--delete"
                            aria-label={t('referralIntro.delete')}
                            disabled={!canUseSms}>
                            ⌫
                        </button>
                    </div>

                    <button
                        type="button"
                        className="referral-intro__secondary-btn"
                        onClick={() => {
                            void onSendSms()
                        }}
                        disabled={!canUseSms || !phoneNumber || isSendingSms}>
                        {isSendingSms
                            ? t('referralIntro.smsSending')
                            : t('referralIntro.smsButton')}
                    </button>

                    {smsFeedback && (
                        <p
                            className={`referral-intro__feedback referral-intro__feedback--${smsFeedback.type}`}>
                            {smsFeedback.message}
                        </p>
                    )}
                </div>
            </section>

            <div className="referral-intro__footer">
                <button
                    type="button"
                    className="referral-intro__finish"
                    onClick={onFinish}>
                    {t('referralIntro.finishButton')}
                </button>
            </div>
        </div>
    )
}
